import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from 'sonner';
import { Download, LogOut, Users, RefreshCw, CheckCircle, Clock, Trash2, CreditCard, Mail, AlertTriangle, Settings } from 'lucide-react';
import axios from 'axios';

// Use relative URL for API calls - works in both preview and production
const BACKEND_URL = '';

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [registrations, setRegistrations] = useState([]);
  const [stats, setStats] = useState({ total: 0, paid: 0, pending: 0 });
  const [isLoading, setIsLoading] = useState(true);
  const [isExporting, setIsExporting] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState({ show: false, id: null, name: '' });
  const [isDeleting, setIsDeleting] = useState(false);
  const [markPaidConfirm, setMarkPaidConfirm] = useState({ show: false, id: null, name: '', email: '' });
  const [isMarkingPaid, setIsMarkingPaid] = useState(false);

  const handleResendNotification = async (id, name) => {
    try {
      await axios.post(`${BACKEND_URL}/api/registrations/${id}/resend-notification`, {}, {
        headers: getAuthHeaders()
      });
      toast.success(`Notification resent for ${name}`);
      fetchRegistrations();
    } catch (err) {
      toast.error(`Failed to resend: ${err.response?.data?.detail || err.message}`);
    }
  };

  const handleEmailDiagnostics = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/registrations/email-diagnostics`, {
        headers: getAuthHeaders()
      });
      const d = response.data.diagnostics;
      if (d.test_email_result === 'sent') {
        toast.success(`Email working! Test sent to ${d.organizer_email}. Check inbox/spam.`);
      } else {
        const envInfo = d.env_file_exists ? `(.env found, keys: ${d.env_file_keys?.join(', ')})` : '(.env file NOT found!)';
        toast.error(`Email FAILED: ${d.test_email_error || d.test_email_result} ${envInfo}`, { duration: 10000 });
      }
    } catch (err) {
      toast.error(`Diagnostics failed: ${err.response?.data?.detail || err.message}`);
    }
  };

  const getAuthHeaders = useCallback(() => {
    const token = localStorage.getItem('adminToken');
    return {
      Authorization: `Bearer ${token}`
    };
  }, []);

  const fetchRegistrations = useCallback(async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/registrations/`, {
        headers: getAuthHeaders()
      });

      if (response.data.success) {
        const data = response.data.data;
        setRegistrations(data);
        
        // Calculate stats
        const paid = data.filter(r => r.paymentStatus === 'paid').length;
        setStats({
          total: data.length,
          paid: paid,
          pending: data.length - paid
        });
      }
    } catch (error) {
      if (error.response?.status === 401) {
        toast.error('Session expired', { description: 'Please login again' });
        localStorage.removeItem('adminToken');
        navigate('/admin');
      } else {
        toast.error('Error fetching registrations');
      }
    } finally {
      setIsLoading(false);
    }
  }, [getAuthHeaders, navigate]);

  useEffect(() => {
    // Check if logged in
    const token = localStorage.getItem('adminToken');
    if (!token) {
      navigate('/admin');
      return;
    }

    fetchRegistrations();
  }, [navigate, fetchRegistrations]);

  const handleExportCSV = async () => {
    setIsExporting(true);
    try {
      const response = await axios.get(`${BACKEND_URL}/api/registrations/export/csv`, {
        headers: getAuthHeaders(),
        responseType: 'blob'
      });

      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `aura2026_registrations_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      toast.success('CSV exported successfully');
    } catch (error) {
      if (error.response?.status === 401) {
        toast.error('Session expired', { description: 'Please login again' });
        localStorage.removeItem('adminToken');
        navigate('/admin');
      } else {
        toast.error('Error exporting CSV');
      }
    } finally {
      setIsExporting(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('adminToken');
    toast.success('Logged out successfully');
    navigate('/admin');
  };

  const handleDeleteClick = (id, name) => {
    setDeleteConfirm({ show: true, id, name });
  };

  const handleDeleteConfirm = async () => {
    if (!deleteConfirm.id) return;
    
    setIsDeleting(true);
    try {
      await axios.delete(`${BACKEND_URL}/api/registrations/${deleteConfirm.id}`, {
        headers: getAuthHeaders()
      });
      
      toast.success('Registration deleted successfully');
      setDeleteConfirm({ show: false, id: null, name: '' });
      fetchRegistrations(); // Refresh the list
    } catch (error) {
      if (error.response?.status === 401) {
        toast.error('Session expired', { description: 'Please login again' });
        localStorage.removeItem('adminToken');
        navigate('/admin');
      } else {
        toast.error('Error deleting registration', {
          description: error.response?.data?.detail || 'Please try again'
        });
      }
    } finally {
      setIsDeleting(false);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteConfirm({ show: false, id: null, name: '' });
  };

  const handleMarkPaidClick = (id, name, email) => {
    setMarkPaidConfirm({ show: true, id, name, email });
  };

  const handleMarkPaidConfirm = async () => {
    if (!markPaidConfirm.id) return;
    
    setIsMarkingPaid(true);
    try {
      await axios.patch(`${BACKEND_URL}/api/registrations/${markPaidConfirm.id}/mark-paid`, {}, {
        headers: getAuthHeaders()
      });
      
      toast.success('Registration marked as paid', {
        description: `Confirmation email sent to ${markPaidConfirm.email}`
      });
      setMarkPaidConfirm({ show: false, id: null, name: '', email: '' });
      fetchRegistrations(); // Refresh the list
    } catch (error) {
      if (error.response?.status === 401) {
        toast.error('Session expired', { description: 'Please login again' });
        localStorage.removeItem('adminToken');
        navigate('/admin');
      } else {
        toast.error('Error marking as paid', {
          description: error.response?.data?.detail || 'Please try again'
        });
      }
    } finally {
      setIsMarkingPaid(false);
    }
  };

  const handleMarkPaidCancel = () => {
    setMarkPaidConfirm({ show: false, id: null, name: '', email: '' });
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-center">
          <RefreshCw className="w-8 h-8 animate-spin text-[#05ab95] mx-auto mb-4" />
          <p className="text-gray-600">Loading registrations...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 shadow-sm">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">AURA 2026 Admin</h1>
              <p className="text-gray-600 text-sm">Registration Management</p>
            </div>
            <Button
              onClick={handleLogout}
              variant="outline"
              className="flex items-center gap-2"
            >
              <LogOut className="w-4 h-4" />
              Logout
            </Button>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-4 py-8">
        {/* Stats Cards */}
        <div className="grid md:grid-cols-3 gap-6 mb-8">
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Total Registrations</p>
                  <p className="text-3xl font-bold text-gray-900">{stats.total}</p>
                </div>
                <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                  <Users className="w-6 h-6 text-blue-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Payment Completed</p>
                  <p className="text-3xl font-bold text-green-600">{stats.paid}</p>
                </div>
                <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                  <CheckCircle className="w-6 h-6 text-green-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Payment Pending</p>
                  <p className="text-3xl font-bold text-yellow-600">{stats.pending}</p>
                </div>
                <div className="w-12 h-12 bg-yellow-100 rounded-full flex items-center justify-center">
                  <Clock className="w-6 h-6 text-yellow-600" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Actions */}
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-xl font-semibold text-gray-900">Registrations</h2>
          <div className="flex gap-3">
            <Button
              onClick={fetchRegistrations}
              variant="outline"
              className="flex items-center gap-2"
            >
              <RefreshCw className="w-4 h-4" />
              Refresh
            </Button>
            <Button
              onClick={handleExportCSV}
              disabled={isExporting}
              className="bg-[#05ab95] hover:bg-[#048c7a] flex items-center gap-2"
            >
              <Download className="w-4 h-4" />
              {isExporting ? 'Exporting...' : 'Export CSV'}
            </Button>
            <Button
              onClick={handleEmailDiagnostics}
              variant="outline"
              className="flex items-center gap-2"
              data-testid="email-diagnostics-btn"
            >
              <Settings className="w-4 h-4" />
              Test Email
            </Button>
          </div>
        </div>

        {/* Table */}
        <Card>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Email</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Designation</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Hospital</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Country</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Phone</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Package</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Payment</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Email</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Submitted</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {registrations.length === 0 ? (
                    <tr>
                      <td colSpan="11" className="px-4 py-8 text-center text-gray-500">
                        No registrations yet
                      </td>
                    </tr>
                  ) : (
                    registrations.map((reg, index) => (
                      <tr key={reg._id || index} className="hover:bg-gray-50">
                        <td className="px-4 py-3 text-sm text-gray-900 font-medium">{reg.name}</td>
                        <td className="px-4 py-3 text-sm text-gray-600">{reg.email}</td>
                        <td className="px-4 py-3 text-sm text-gray-600">{reg.designation}</td>
                        <td className="px-4 py-3 text-sm text-gray-600">{reg.hospital}</td>
                        <td className="px-4 py-3 text-sm text-gray-600">{reg.country}</td>
                        <td className="px-4 py-3 text-sm text-gray-600">{reg.phone}</td>
                        <td className="px-4 py-3 text-sm text-gray-600">
                          <span className="font-medium">{reg.registrationDays || 2}D</span>
                          <span className="text-gray-400 mx-1">|</span>
                          <span className="text-xs">{reg.selectedDate || 'Both Days'}</span>
                          <span className="block text-xs text-[#05ab95] font-semibold">AED {reg.amount || 1200}</span>
                        </td>
                        <td className="px-4 py-3 text-sm">
                          {reg.paymentStatus === 'paid' ? (
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                              <CheckCircle className="w-3 h-3 mr-1" />
                              Paid
                            </span>
                          ) : (
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                              <Clock className="w-3 h-3 mr-1" />
                              Pending
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-sm">
                          {reg.emailStatus === 'sent' ? (
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                              <Mail className="w-3 h-3 mr-1" />
                              Sent
                            </span>
                          ) : reg.emailStatus === 'failed' ? (
                            <button
                              onClick={() => handleResendNotification(reg._id, reg.name)}
                              className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800 hover:bg-red-200 cursor-pointer"
                              title={reg.emailError || 'Click to resend'}
                            >
                              <AlertTriangle className="w-3 h-3 mr-1" />
                              Failed - Retry
                            </button>
                          ) : (
                            <button
                              onClick={() => handleResendNotification(reg._id, reg.name)}
                              className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600 hover:bg-gray-200 cursor-pointer"
                              title="Click to send notification"
                            >
                              <Mail className="w-3 h-3 mr-1" />
                              N/A - Send
                            </button>
                          )}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-600">{formatDate(reg.submittedAt)}</td>
                        <td className="px-4 py-3 text-sm">
                          <div className="flex items-center gap-1">
                            {reg.paymentStatus !== 'paid' && (
                              <button
                                onClick={() => handleMarkPaidClick(reg._id, reg.name, reg.email)}
                                className="text-green-600 hover:text-green-800 hover:bg-green-50 p-2 rounded-md transition-colors"
                                title="Mark as Paid"
                              >
                                <CreditCard className="w-4 h-4" />
                              </button>
                            )}
                            <button
                              onClick={() => handleDeleteClick(reg._id, reg.name)}
                              className="text-red-600 hover:text-red-800 hover:bg-red-50 p-2 rounded-md transition-colors"
                              title="Delete registration"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      </main>

      {/* Delete Confirmation Modal */}
      {deleteConfirm.show && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full p-6">
            <div className="text-center">
              <div className="mx-auto mb-4 w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
                <Trash2 className="w-6 h-6 text-red-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">Delete Registration</h3>
              <p className="text-gray-600 mb-6">
                Are you sure you want to delete the registration for <strong>{deleteConfirm.name}</strong>? This action cannot be undone.
              </p>
              <div className="flex gap-3 justify-center">
                <Button
                  onClick={handleDeleteCancel}
                  variant="outline"
                  disabled={isDeleting}
                  className="px-6"
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleDeleteConfirm}
                  disabled={isDeleting}
                  className="bg-red-600 hover:bg-red-700 text-white px-6"
                >
                  {isDeleting ? 'Deleting...' : 'Delete'}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Mark as Paid Confirmation Modal */}
      {markPaidConfirm.show && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full p-6">
            <div className="text-center">
              <div className="mx-auto mb-4 w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                <CreditCard className="w-6 h-6 text-green-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">Mark as Paid</h3>
              <p className="text-gray-600 mb-2">
                Mark registration for <strong>{markPaidConfirm.name}</strong> as paid?
              </p>
              <p className="text-sm text-gray-500 mb-6">
                A confirmation email will be sent to {markPaidConfirm.email}
              </p>
              <div className="flex gap-3 justify-center">
                <Button
                  onClick={handleMarkPaidCancel}
                  variant="outline"
                  disabled={isMarkingPaid}
                  className="px-6"
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleMarkPaidConfirm}
                  disabled={isMarkingPaid}
                  className="bg-green-600 hover:bg-green-700 text-white px-6"
                >
                  {isMarkingPaid ? 'Processing...' : 'Confirm Payment'}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminDashboard;
